import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ScenarioService } from '../modules/scenario/services/scenario.service';
import { StepsService } from '../modules/steps/services/steps.service';
import { TestExecutorService, StepMetadata } from './test-executor.service';
import { ExecutionResultService } from './execution-result.service';
import { SessionPoolService } from './session-pool.service';
import { GapAnalysisElectronService, ScenarioConfig } from './gap-analysis-electron.service';
import { ReportApiService, ReportPayload } from '../modules/report/services/report-api.service';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

interface RunnerPayload {
  runId: string;
  apiBase?: string; // not used directly here; Angular services use environment.apiUrl
  authToken?: string | null;
  tenantId?: string | null;
}

interface JobPayload {
  jobId: string;
  scenarioIds: string[];
  apiBase?: string;
  authToken?: string | null;
  tenantId?: string | null;
  parallelExecution?: boolean;
  maxParallelChains?: number;
}

interface ExecutionPayload {
  jobId: string;
  mode: 'gap-execution' | 'generation-execution';
  apiBase: string;
  authToken?: string | null;
  tenantUrl: string;
  tenantId?: string | null;
  cloneId?: string;
  generationJobId?: string;
  recordingIdsJson?: string;
  maxParallel?: number;
  scenarioOffset?: number;
  scenarioLimit?: number;
}

interface ScenarioChain {
  chainId: number;
  scenarios: any[];
  authKey: string | null;  // Auth profile key for this chain
}

@Injectable({ providedIn: 'root' })
export class RunnerBridgeService {
  private initialized = false;
  private listenerAttached = false;
  private attachAttempts = 0;
  private reportStatusPassedId: string | null = null;
  private reportStatusFailedId: string | null = null;
  private currentRunTenantId: string | null = null;

  constructor(
    private scenarioService: ScenarioService,
    private stepsService: StepsService,
    private testExecutor: TestExecutorService,
    private executionResultService: ExecutionResultService,
    private http: HttpClient,
    private sessionPool: SessionPoolService,
    private reportApiService: ReportApiService,
    private gapAnalysisElectron: GapAnalysisElectronService
  ) {
    (window as any).__RB_CONSTRUCTED = true;
    this.initRunnerListener();
  }

  private async loadReportStatusIds(): Promise<void> {
    if (this.reportStatusPassedId && this.reportStatusFailedId) return;
    try {
      const dataResult = await firstValueFrom(this.executionResultService.getReferenceList(['TEACC']));
      if (Array.isArray(dataResult)) {
        const passed = dataResult.find((r: any) => r.referenceValue?.toLowerCase() === 'passed');
        const failed = dataResult.find((r: any) => r.referenceValue?.toLowerCase() === 'failed');
        this.reportStatusPassedId = passed?.id || null;
        this.reportStatusFailedId = failed?.id || null;
      }
    } catch {}
  }

  private initRunnerListener() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      const host: any = (window as any).host;
      if (!host) return;

      const isRunnerMode = window.location.search.includes('runner=1');
      if (!isRunnerMode) return;

      if (host?.onRunnerExecuteScenario) {
        host.onRunnerExecuteScenario((payload: RunnerPayload) => {
          this.handleRunnerExecute(payload).catch(e => {
            throw new Error(`Runner execute error: ${e.message}`);
          });
        });
      }

      this.tryConsumeRunnerPayload();
    } catch (e) {
      throw new Error(`initRunnerListener failed: ${e}`);
    }
  }

  private async tryConsumeRunnerPayload() {
    try {
      // Parse payload from URL query parameters (embedded by electron.main.js)
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode'); // 'job' or 'scenario'
      const runId = urlParams.get('runId');
      const jobId = urlParams.get('jobId');
      const scenarioIdsJson = urlParams.get('scenarioIdsJson');
      const apiBase = urlParams.get('apiBase');
      const authToken = urlParams.get('authToken');
      const tenantId = urlParams.get('tenantId');

      if (mode === 'gap-execution' || mode === 'generation-execution') {
        // GAP/GENERATION EXECUTION MODE: Execute step arrays against tenant
        const tenantUrl = urlParams.get('tenantUrl') || '';
        const cloneId = urlParams.get('cloneId') || undefined;
        const generationJobId = urlParams.get('generationJobId') || undefined;
        const recordingIdsJson = urlParams.get('recordingIdsJson') || undefined;
        const maxParallel = urlParams.get('maxParallel') ? parseInt(urlParams.get('maxParallel')!, 10) : undefined;
        const scenarioOffset = urlParams.get('scenarioOffset') ? parseInt(urlParams.get('scenarioOffset')!, 10) : undefined;
        const scenarioLimit = urlParams.get('scenarioLimit') ? parseInt(urlParams.get('scenarioLimit')!, 10) : undefined;

        const payload: ExecutionPayload = {
          jobId: jobId || '',
          mode: mode as 'gap-execution' | 'generation-execution',
          apiBase: apiBase || '',
          authToken: authToken || undefined,
          tenantUrl,
          tenantId: tenantId || undefined,
          cloneId,
          generationJobId,
          recordingIdsJson,
          maxParallel,
          scenarioOffset,
          scenarioLimit,
        };
        await this.handleGapExecution(payload);
      } else if (mode === 'job' && jobId && scenarioIdsJson) {
        // NEW MODE: Job execution with multiple scenarios and session reuse
        try {
          const scenarioIds = JSON.parse(decodeURIComponent(scenarioIdsJson));
          const payload: JobPayload = {
            jobId,
            scenarioIds,
            apiBase: apiBase || undefined,
            authToken: authToken || undefined,
            tenantId: tenantId || undefined,
            parallelExecution: urlParams.get('parallelExecution') !== 'false',
            maxParallelChains: parseInt(urlParams.get('maxParallelChains') || '5', 10)
          };
          await this.handleJobExecute(payload);
        } catch (parseError) {
          throw new Error(`Invalid job payload: ${parseError}`);
        }
      } else if (runId) {
        // OLD MODE: Single scenario execution (backward compatible)
        const payload: RunnerPayload = {
          runId,
          apiBase: apiBase || undefined,
          authToken: authToken || undefined,
          tenantId: tenantId || undefined
        };
        await this.handleRunnerExecute(payload);
      }
    } catch (e) {
      throw new Error(`tryConsumeRunnerPayload failed: ${e}`);
    }
  }

  /**
   * Resolve the auth profile key for a scenario.
   * Returns authProfileId from direct field or authProfileInfo JSON, or null if no auth.
   */
  private resolveAuthKey(scenario: any): string | null {
    if (scenario.authProfileId) return scenario.authProfileId;
    if (scenario.authProfileInfo) {
      try {
        const info = typeof scenario.authProfileInfo === 'string'
          ? JSON.parse(scenario.authProfileInfo)
          : scenario.authProfileInfo;
        if (info?.authProfileIds?.length > 0) return info.authProfileIds[0];
      } catch { /* ignore parse errors */ }
    }
    return null;
  }

  /**
   * Two-level session policy resolution.
   * Level 1: Use explicit sessionPolicy field (FRESH/REUSE).
   * Level 2: If null, auto-detect from first steps — login steps = FRESH, else REUSE.
   */
  private async resolveEffectiveSessionPolicy(scenario: any, apiBase: string): Promise<'FRESH' | 'REUSE'> {
    const sid = scenario.id || scenario.scenarioId;
    // Level 1: Explicit policy wins
    if (scenario.sessionPolicy === 'FRESH') {
      console.log(`[PolicyResolve] ${sid}: Level 1 → FRESH (explicit)`);
      return 'FRESH';
    }
    if (scenario.sessionPolicy === 'REUSE') {
      console.log(`[PolicyResolve] ${sid}: Level 1 → REUSE (explicit)`);
      return 'REUSE';
    }

    // Level 2: Auto-detect from first steps (sessionPolicy is null)
    console.log(`[PolicyResolve] ${sid}: sessionPolicy=${scenario.sessionPolicy}, checking first steps...`);
    try {
      const steps = await this.fetchFirstSteps(scenario, apiBase, 3);
      console.log(`[PolicyResolve] ${sid}: fetched ${steps.length} steps:`, steps.map((s: any) => s.stepDescription));
      const hasLogin = this.hasLoginSteps(steps);
      console.log(`[PolicyResolve] ${sid}: hasLoginSteps=${hasLogin}`);
      if (hasLogin) return 'FRESH';
    } catch (err: any) {
      console.error(`[PolicyResolve] ${sid}: step fetch FAILED:`, err?.message || err);
    }
    console.log(`[PolicyResolve] ${sid}: defaulting to REUSE`);
    return 'REUSE';
  }

  private async fetchFirstSteps(scenario: any, apiBase: string, limit: number): Promise<any[]> {
    const base = apiBase.replace(/\/$/, '');
    const scenarioId = scenario.id || scenario.scenarioId;
    const resp = await firstValueFrom(this.http.get<any>(`${base}/steps/scenario/${scenarioId}`));
    const steps = Array.isArray(resp) ? resp : (resp?.data || []);
    return steps
      .filter((s: any) => s.isActive !== false)
      .sort((a: any, b: any) => (a.stepId || 0) - (b.stepId || 0))
      .slice(0, limit);
  }

  private hasLoginSteps(steps: any[]): boolean {
    // Match multiple formats:
    // "Enter the username as stud_isu" (recorded step — "as" keyword)
    // "Enter stud_isu in username" (natural language — "in/into" keyword)
    // "Enter the password as xyz" / "Enter xyz in password"
    const usernamePattern = /enter\s+(?:the\s+)?(?:user\s*name|username|user\s*id)\s+(?:as|in|into)\s+/i;
    const usernamePattern2 = /enter\s+.+\s+(?:in|into|as)\s+(?:the\s+)?(?:user\s*name|username|user\s*id|user)/i;
    const passwordPattern = /enter\s+(?:the\s+)?password\s+(?:as|in|into)\s+/i;
    const passwordPattern2 = /enter\s+.+\s+(?:in|into|as)\s+(?:the\s+)?password/i;
    const descriptions = steps.map(s => s.stepDescription || '').filter(Boolean);
    const hasUsername = descriptions.some(d => usernamePattern.test(d) || usernamePattern2.test(d));
    const hasPassword = descriptions.some(d => passwordPattern.test(d) || passwordPattern2.test(d));
    return hasUsername && hasPassword;
  }

  /**
   * Group scenarios into dependency chains based on sessionPolicy and auth profile.
   * - REUSE + known authProfileId → append to chain with matching auth profile
   * - REUSE + no auth → positional fallback (append to last chain)
   * - null or FRESH → always start new chain
   * Chains can run in parallel; scenarios within a chain run sequentially.
   */
  private buildScenarioChains(scenarios: any[]): ScenarioChain[] {
    const chainsByAuth = new Map<string, ScenarioChain>();
    const allChains: ScenarioChain[] = [];
    let lastChain: ScenarioChain | null = null;

    for (const scenario of scenarios) {
      const authKey = this.resolveAuthKey(scenario);
      const policy = scenario._effectivePolicy || scenario.sessionPolicy; // resolved or raw policy

      if (policy === 'REUSE') {
        if (authKey && chainsByAuth.has(authKey)) {
          // REUSE + known auth → append to matching auth chain
          const matchingChain = chainsByAuth.get(authKey)!;
          matchingChain.scenarios.push(scenario);
          lastChain = matchingChain;
        } else if (lastChain) {
          // REUSE + no auth (no preconditions) → positional fallback
          lastChain.scenarios.push(scenario);
        } else {
          // REUSE but nothing to reuse → new chain
          const chain: ScenarioChain = { chainId: allChains.length, scenarios: [scenario], authKey };
          allChains.push(chain);
          lastChain = chain;
          if (authKey) chainsByAuth.set(authKey, chain);
        }
      } else {
        // null or FRESH → always start new chain
        const chain: ScenarioChain = { chainId: allChains.length, scenarios: [scenario], authKey };
        allChains.push(chain);
        lastChain = chain;
        if (authKey) chainsByAuth.set(authKey, chain); // Latest chain for this auth wins
      }
    }

    return allChains;
  }

  /** Max parallel chains to prevent memory exhaustion (each chain = a Chromium BrowserWindow) */
  private static readonly MAX_PARALLEL_CHAINS = 3;

  /**
   * Execute async tasks with a concurrency limit.
   * Maintains a pool of `limit` active promises; starts next on resolve.
   */
  private async runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
    const results: T[] = new Array(tasks.length);
    let nextIndex = 0;

    const runNext = async (): Promise<void> => {
      while (nextIndex < tasks.length) {
        const idx = nextIndex++;
        results[idx] = await tasks[idx]();
      }
    };

    const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext());
    await Promise.all(workers);
    return results;
  }

  /**
   * Execute a single chain: scenarios run sequentially within the chain.
   * Each chain manages its own session state (no singleton mutation).
   */
  private async executeChain(
    chain: ScenarioChain, context: any, apiBase: string
  ): Promise<{ success: boolean; chainId: number; failedScenarioId?: string; error?: string }> {
    const tag = `[Chain ${chain.chainId}]`;
    console.log(`${tag} Starting chain with ${chain.scenarios.length} scenario(s), authKey=${chain.authKey || 'none'}`);

    let previousSession: any = null;

    try {
      for (let i = 0; i < chain.scenarios.length; i++) {
        const scenario = chain.scenarios[i];
        const scenarioId = scenario.id || scenario.scenarioId;
        console.log(`${tag} Executing scenario ${scenarioId} (${i + 1}/${chain.scenarios.length})`);

        try {
          const result = await this.executeScenarioInChain(scenario, context, apiBase, {
            previousSession,
            chainId: chain.chainId
          });
          previousSession = result.session;
        } catch (err: any) {
          console.error(`${tag} Scenario ${scenarioId} FAILED:`, err?.message || err);
          return { success: false, chainId: chain.chainId, failedScenarioId: scenarioId, error: err?.message || String(err) };
        }
      }

      console.log(`${tag} All ${chain.scenarios.length} scenario(s) completed successfully`);
      return { success: true, chainId: chain.chainId };
    } finally {
      // Release this chain's session window as soon as the chain finishes, instead of
      // letting every completed chain's window linger until destroyAll(). Without this,
      // jobs with many FRESH/distinct-auth scenarios accumulate one window per chain and
      // exhaust Electron memory (same OOM that capped gap analysis at ~31).
      if (previousSession?.windowHandle) {
        await this.sessionPool.releaseSession({
          authProfileId: previousSession.authProfileId,
          tenantId: previousSession.tenantId
        }).catch(() => {});
      }
    }
  }

  private async handleGapExecution(payload: ExecutionPayload) {
    const { jobId, mode, apiBase, authToken, tenantUrl, cloneId, generationJobId, recordingIdsJson, maxParallel, scenarioOffset, scenarioLimit } = payload;

    try {
      if (authToken) {
        localStorage.setItem('authToken', authToken);
        sessionStorage.setItem('authToken', authToken);
      }

      const base = (apiBase || '').replace(/\/$/, '');
      this.sessionPool.setApiBaseUrl(base);

      // Fetch recordings based on mode
      let recordings: any[] = [];

      if (mode === 'gap-execution' && cloneId) {
        const resp = await firstValueFrom(this.http.get<any>(`${base}/clones/${cloneId}/injected-recordings`));
        recordings = resp?.data || resp || [];
      } else if (mode === 'generation-execution' && generationJobId) {
        const resp = await firstValueFrom(this.http.get<any>(`${base}/step-generation/jobs/${generationJobId}/gen-steps-recordings`));
        recordings = resp?.data || resp || [];
        // Filter by recordingIds if specified
        if (recordingIdsJson) {
          try {
            const ids = JSON.parse(decodeURIComponent(recordingIdsJson));
            if (Array.isArray(ids) && ids.length > 0) {
              recordings = recordings.filter((r: any) => ids.includes(r.id));
            }
          } catch {}
        }
      }

      if (!recordings.length) {
        throw new Error('No recordings available for execution');
      }

      // Convert to ScenarioConfig[] (same format GapAnalysisElectronService expects)
      let scenarios: ScenarioConfig[] = recordings.map((rec: any, idx: number) => {
        const steps = typeof rec.steps === 'string' ? JSON.parse(rec.steps) : (rec.steps || []);
        return {
          scenarioId: `GAP-${String(idx + 1).padStart(3, '0')}`,
          scenarioName: rec.recordingName || rec.runName || `Recording ${idx + 1}`,
          steps,
          loginSteps: [],
          navDefaultSelections: {},
          expectedElements: [],
        };
      });

      // Apply batch slicing (Electron process recycling: each batch is executed in a separate Electron process)
      const offset = scenarioOffset ?? 0;
      const limit = scenarioLimit ?? scenarios.length;
      const sliced = scenarios.slice(offset, offset + limit);
      if (sliced.length < scenarios.length || offset > 0) {
        console.log(`[GapExecution] Processing batch: scenarios ${offset + 1}-${offset + sliced.length} of ${scenarios.length} total`);
      }
      scenarios = sliced;

      // Build callbacks that save results to API
      // Note: callbacks receive local index (0-based within batch); convert to global using offset
      const savedIdMap = new Map<number, string>();
      const callbacks = {
        onScenarioStart: (localIndex: number, scenarioId: string, scenarioName: string) => {
          const globalIndex = offset + localIndex;
          if (mode === 'gap-execution' && cloneId) {
            const scenarioResult = { scenarioId, scenarioName, result: 'pending', stepDetail: 'Running...', failedStep: 0 };
            firstValueFrom(this.http.post<any>(`${base}/clones/${cloneId}/gap-results`, { results: [scenarioResult] }))
              .then(res => {
                const saved = res?.data?.[0] || res?.[0];
                if (saved?.id) savedIdMap.set(globalIndex, saved.id);
              }).catch(() => {});
          }
        },
        onStepComplete: (localIndex: number, scenarioId: string, stepResult: any) => {
          const globalIndex = offset + localIndex;
          if (mode === 'gap-execution' && cloneId) {
            const resultId = savedIdMap.get(globalIndex);
            if (resultId) {
              firstValueFrom(this.http.post(`${base}/clones/${cloneId}/gap-results/${resultId}/save-gap-steps`, { stepResults: [stepResult] }))
                .catch(() => {});
            }
          }
        },
        onScenarioComplete: (localIndex: number, scenarioId: string, result: any) => {
          const globalIndex = offset + localIndex;
          if (mode === 'gap-execution' && cloneId) {
            const resultId = savedIdMap.get(globalIndex);
            if (resultId) {
              firstValueFrom(this.http.patch(`${base}/clones/${cloneId}/gap-results/${resultId}`, { result: result.result, stepDetail: result.stepDetail }))
                .catch(() => {});
            }
          }
        }
      };

      // Execute using existing GapAnalysisElectronService.
      // gap-execution persists each step's screenshot per-step (onStepComplete), so the retained
      // stepResults can drop screenshots to avoid renderer OOM on large runs. generation-execution
      // has no per-step save and bulk-uploads stepResults (with screenshots) below, so it must keep them.
      const execResults = await this.gapAnalysisElectron.runGapAnalysis(
        cloneId || generationJobId || jobId,
        tenantUrl,
        scenarios,
        [],
        callbacks,
        maxParallel,
        mode === 'gap-execution'
      );

      // Save results for generation-execution mode
      if (mode === 'generation-execution' && generationJobId && execResults?.length) {
        try {
          const recordingIds = recordings.map((r: any) => r.id);
          const runResp = await firstValueFrom(this.http.post<any>(`${base}/step-generation/jobs/${generationJobId}/execute`, { recordingIds }));
          const runId = runResp?.runId;
          if (runId) {
            const results = recordings.map((rec: any, i: number) => {
              const result = execResults[i] as any;
              if (!result) return null;
              const stepResults = result.stepResults || [];
              return {
                recordingId: rec.id,
                status: result.result === 'pass' ? 'passed' : 'failed',
                stepResults,
                totalSteps: stepResults.length,
                passedSteps: stepResults.filter((s: any) => s.result === 'pass').length,
                failedSteps: stepResults.filter((s: any) => s.result === 'fail' || s.result === 'mismatch').length,
                failedAtStep: stepResults.findIndex((s: any) => s.result === 'fail') + 1 || null,
                errorMessage: result.result !== 'pass' ? (result.stepDetail || `Failed at step ${result.failedStep}`) : null,
                durationMs: stepResults.reduce((sum: number, s: any) => sum + (s.durationMs || 0), 0),
              };
            }).filter(Boolean);
            await firstValueFrom(this.http.post(`${base}/step-generation/runs/${runId}/results`, { results, clientId: cloneId || 'default' }));
          }
        } catch (e) {
          console.error('[RunnerBridge] Failed to save generation execution results:', e);
        }
      }

      // Notify Electron main process
      const host: any = (window as any).host;
      if (host?.notifyRunnerDone) {
        host.notifyRunnerDone({ jobId, success: true });
      }
    } catch (e: any) {
      const host: any = (window as any).host;
      if (host?.notifyRunnerDone) {
        host.notifyRunnerDone({ jobId, success: false, error: e?.message || String(e) });
      }
    }
  }

  private async handleJobExecute(payload: JobPayload) {
    const { jobId, scenarioIds, apiBase, authToken, tenantId } = payload;
    this.currentRunTenantId = tenantId || null;

    try {
      // Inject auth token if provided
      if (authToken) {
        localStorage.setItem('authToken', authToken);
        sessionStorage.setItem('authToken', authToken);
      }

      // Fetch scenarios using existing APIs (no special context endpoint needed)
      const base = (apiBase || '').replace(/\/$/, '');
      
      // Fetch all scenarios in parallel
      console.log(`[RunnerBridge] fetching ${scenarioIds.length} scenario(s) from ${base}/scenarios/...`);
      let scenarios: any[];
      try {
        const scenarioPromises = scenarioIds.map(id =>
          firstValueFrom(this.http.get<any>(`${base}/scenarios/${id}`))
        );
        scenarios = await Promise.all(scenarioPromises);
        console.log(`[RunnerBridge] fetched ${scenarios.length} scenario(s) successfully`);
      } catch (err: any) {
        console.error('[RunnerBridge] Failed to fetch scenarios:', err?.status, err?.message, err?.error);
        throw err;
      }

      // Extract unique auth profiles and screens from scenarios
      const authProfileIds = new Set<string>();
      const screenIds = new Set<string>();
      
      scenarios.forEach((scenario: any) => {
        if (scenario.authProfileId) authProfileIds.add(scenario.authProfileId);
        if (scenario.startScreenId) screenIds.add(scenario.startScreenId);
        // Note: authProfileInfo with profileGroup is resolved at execution time by session-pool
      });

      // Fetch auth profiles and screens if needed (using existing APIs)
      let authProfiles: any[] = [];
      let screens: any[] = [];
      
      if (authProfileIds.size > 0) {
        // Fetch all auth profiles (or just the ones we need if there's a bulk endpoint)
        try {
          const profilesResp = await firstValueFrom(this.http.get<any>(`${base}/auth-profiles`));
          const allProfiles = Array.isArray(profilesResp) ? profilesResp : (profilesResp?.data || []);
          authProfiles = allProfiles.filter((p: any) => authProfileIds.has(p.id));
        } catch (err: any) {
        }
      }

      if (screenIds.size > 0) {
        try {
          const screensResp = await firstValueFrom(this.http.get<any>(`${base}/screens`));
          const allScreens = Array.isArray(screensResp) ? screensResp : (screensResp?.data || []);
          screens = allScreens.filter((s: any) => screenIds.has(s.id));
        } catch (err: any) {
        }
      }

      // Build context object (same structure as the non-existent context endpoint would return)
      const context = {
        scenarios,
        authProfiles,
        screens,
        modules: [] // Modules will be generated dynamically from auth profiles
      };

      // Set runtime API base URL for session pool (runner mode uses --apiBase, not environment.apiUrl)
      if (apiBase) {
        this.sessionPool.setApiBaseUrl(apiBase);
      }

      // Initialize session pool with EMPTY authProfiles (match local mode behavior)
      // Each scenario will fetch its own auth profile during precondition execution
      await this.sessionPool.initialize([], context.screens || []);

      // Build ordered scenario list preserving scenarioIds order
      const orderedScenarios = scenarioIds
        .map(id => context.scenarios.find((s: any) => s.id === id))
        .filter(Boolean);

      const parallelEnabled = payload.parallelExecution !== false;
      const maxChains = payload.maxParallelChains || 5;
      console.log(`[Job ${jobId}] Config: parallelExecution=${payload.parallelExecution} (enabled=${parallelEnabled}), maxChains=${maxChains}`);

      // Resolve effective session policies for all scenarios (parallel fetch)
      console.log(`[Job ${jobId}] Resolving session policies for ${orderedScenarios.length} scenario(s)...`);
      const resolvedPolicies = await Promise.all(
        orderedScenarios.map(s => this.resolveEffectiveSessionPolicy(s, apiBase!))
      );
      orderedScenarios.forEach((s: any, i: number) => {
        s._effectivePolicy = resolvedPolicies[i];
        if (!s.sessionPolicy && resolvedPolicies[i] !== 'REUSE') {
          console.log(`[Job ${jobId}] Auto-detected FRESH for scenario ${s.id} (login steps found)`);
        }
      });

      let allSucceeded = true;
      let chainResults: any[] = [];

      if (parallelEnabled) {
        // PARALLEL MODE: Group into chains and execute concurrently
        const chains = this.buildScenarioChains(orderedScenarios);
        console.log(`[Job ${jobId}] PARALLEL mode: ${orderedScenarios.length} scenarios → ${chains.length} chain(s), max concurrency=${maxChains}`);
        chains.forEach(c => console.log(`  Chain ${c.chainId}: ${c.scenarios.length} scenario(s), auth=${c.authKey || 'none'}`));

        // Stagger parallel chain starts to reduce network contention with Workday's heavy bundle
        const chainTasks = chains.map((chain, index) => async () => {
          if (index > 0) {
            await new Promise(r => setTimeout(r, 500 * index));
          }
          return this.executeChain(chain, context, apiBase!);
        });
        chainResults = await this.runWithConcurrency(chainTasks, maxChains);
        allSucceeded = chainResults.every((r: any) => r.success);
      } else {
        // SEQUENTIAL MODE: Execute all scenarios in order with two-level policy
        console.log(`[Job ${jobId}] SEQUENTIAL mode: ${orderedScenarios.length} scenario(s)`);
        for (let i = 0; i < orderedScenarios.length; i++) {
          const scenario = orderedScenarios[i];
          if (!scenario) { allSucceeded = false; continue; }

          try {
            // Use the resolved policy to decide session behavior
            scenario.sessionPolicy = scenario._effectivePolicy;
            await this.executeScenarioWithSessionPool(scenario, context, apiBase);
          } catch (err: any) {
            allSucceeded = false;
            break;
          }
        }
      }

      // Cleanup all sessions
      await this.sessionPool.destroyAll();

      // Update job status in API (triggers completion notification)
      await this.updateJobStatus(jobId, allSucceeded ? 'completed' : 'failed', apiBase);

      // Notify Electron main process
      const host: any = (window as any).host;
      if (host?.notifyRunnerDone) {
        const notifyPayload = {
          jobId,
          success: allSucceeded,
          error: allSucceeded ? null : parallelEnabled
            ? `${chainResults.filter((r: any) => !r.success).length} chain(s) failed`
            : 'One or more scenarios failed',
          chains: parallelEnabled ? chainResults : undefined
        };
        host.notifyRunnerDone(notifyPayload);
      }

    } catch (e: any) {
      await this.updateJobStatus(jobId, 'failed', apiBase).catch(() => {});
      const host: any = (window as any).host;
      if (host?.notifyRunnerDone) {
        host.notifyRunnerDone({ jobId, success: false, error: e?.message || String(e) });
      }
    }
  }

  private async updateJobStatus(jobId: string, status: string, apiBase?: string): Promise<void> {
    if (!jobId) return;
    try {
      const base = (apiBase || '').replace(/\/$/, '') || (window as any).__API_BASE__ || '';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';
      const url = base
        ? `${base}/runner/jobs/${jobId}/status`
        : `/api/runner/jobs/${jobId}/status`;
      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
    } catch (_err) {
      // non-fatal — job status update failure must not break execution flow
    }
  }

  /**
   * Execute a single scenario within a chain (parallel-safe).
   * Accepts previous session explicitly, returns session for next scenario.
   * Does NOT mutate singleton state (testExecutor.setSession or executionResultService.setExecutionContext).
   */
  private async executeScenarioInChain(
    scenario: any, context: any, apiBase: string,
    chainOptions: { previousSession?: any; chainId: number }
  ): Promise<{ session: any }> {
    const { previousSession, chainId } = chainOptions;

    // Check if preconditions are required
    const hasAuthProfileInfo = scenario.authProfileInfo && (() => {
      try {
        const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
        return !!(info?.profileGroup && info?.authProfileIds?.length > 0);
      } catch { return false; }
    })();
    const hasPreconditions = scenario.authProfileId || hasAuthProfileInfo || scenario.screenId || scenario.startScreenId;
    const canReuseSession = previousSession && !hasPreconditions && scenario.sessionPolicy !== 'FRESH';

    let session: any;

    if (hasPreconditions) {
      let resolvedAuthProfileId = scenario.authProfileId;
      if (!resolvedAuthProfileId && scenario.authProfileInfo) {
        try {
          const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
          if (info?.authProfileIds?.length > 0) {
            resolvedAuthProfileId = info.authProfileIds[0];
          }
        } catch { /* ignore parse errors */ }
      }
      // Append chainId to prevent cross-chain session pool collisions
      const sessionKey = {
        authProfileId: `${resolvedAuthProfileId || 'default'}-chain${chainId}`,
        tenantId: scenario.tenantId
      };

      const sessionPolicy = scenario.sessionPolicy || 'REUSE';
      session = await this.sessionPool.acquireSession(sessionKey, sessionPolicy);

      const preconditionResult = await this.sessionPool.executePreconditions(scenario, session, context.modules || []);
      if (!preconditionResult.success) {
        throw new Error(`Preconditions failed: ${preconditionResult.error}`);
      }
    } else if (canReuseSession) {
      session = previousSession;
    } else {
      const sessionKey = {
        authProfileId: `no-auth-chain${chainId}`,
        tenantId: scenario.tenantId || 'default'
      };
      const elseSessionPolicy = scenario.sessionPolicy === 'FRESH' ? 'FRESH' : 'REUSE';
      session = await this.sessionPool.acquireSession(sessionKey, elseSessionPolicy);

      const initialUrl = scenario.url;
      if (initialUrl && initialUrl !== 'about:blank') {
        const host: any = (window as any).host;
        if (host?.navigateSession && session.windowHandle) {
          try {
            await host.navigateSession(session.windowHandle, initialUrl);
            await new Promise(resolve => setTimeout(resolve, 3000));
          } catch (navError: any) {
            throw new Error(`Navigation failed: ${navError.message}`);
          }
        }
      }
    }

    // Build execution context explicitly (no singleton mutation)
    const scenarioId = scenario.id || scenario.scenarioId;
    const functionalAreaId =
      scenario?.functionalAreaId ||
      scenario?.functionalArea?.id ||
      undefined;
    const clientPackageMappingId =
      scenario?.clientPackageMappingId ||
      scenario?.clientPackageMapping?.id ||
      scenario?.functionalArea?.clientPackageMappingId ||
      scenario?.functionalArea?.clientPackageMapping?.id ||
      undefined;
    const executionRunId = uuidv4();
    const tenantId = this.currentRunTenantId || scenario?.tenantId || undefined;
    const executionContext = { functionalAreaId, scenarioId, executionRunId, clientPackageMappingId, tenantId };
    console.log('[RunnerBridge] executionContext built:', JSON.stringify(executionContext));
    // Also stamp singleton so saves with no explicit context still work
    this.executionResultService.setExecutionContext(functionalAreaId as any, scenarioId as any, clientPackageMappingId, tenantId);

    // Execute steps with explicit session and context
    await this.executeStepsWithMetadata(scenarioId, scenario, apiBase, session, executionContext);

    return { session };
  }

  /** @deprecated Use executeScenarioInChain for parallel execution. Kept for legacy handleRunnerExecute. */
  private async executeScenarioWithSessionPool(scenario: any, context: any, apiBase?: string): Promise<void> {
    // Check if preconditions are required (match local mode logic)
    const hasAuthProfileInfo = scenario.authProfileInfo && (() => {
      try {
        const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
        return !!(info?.profileGroup && info?.authProfileIds?.length > 0);
      } catch { return false; }
    })();
    const hasPreconditions = scenario.authProfileId || hasAuthProfileInfo || scenario.screenId || scenario.startScreenId;
    
    // Check if there's an existing session from previous scenario
    const existingSession = this.testExecutor.getSession();
    const canReuseSession = existingSession && !hasPreconditions && scenario.sessionPolicy !== 'FRESH';
    
    let session: any;
    
    if (hasPreconditions) {
      // Scenario has its own preconditions - acquire/create dedicated session
      // Resolve authProfileId from authProfileInfo when using profile groups
      let resolvedAuthProfileId = scenario.authProfileId;
      if (!resolvedAuthProfileId && scenario.authProfileInfo) {
        try {
          const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
          if (info?.authProfileIds?.length > 0) {
            resolvedAuthProfileId = info.authProfileIds[0];
          }
        } catch { /* ignore parse errors */ }
      }
      const sessionKey = {
        authProfileId: resolvedAuthProfileId || 'default',
        tenantId: scenario.tenantId
      };
      
      const sessionPolicy = scenario.sessionPolicy || 'REUSE';
      session = await this.sessionPool.acquireSession(sessionKey, sessionPolicy);

      // Execute preconditions
      const preconditionResult = await this.sessionPool.executePreconditions(scenario, session, context.modules || []);
      
      if (!preconditionResult.success) {
        throw new Error(`Preconditions failed: ${preconditionResult.error}`);
      }
      
      // Set session for test executor
      this.testExecutor.setSession(session);
    } else if (canReuseSession) {
      // Reuse existing session from previous scenario (like local mode does)
      session = existingSession;
      // Session is already set in testExecutor, no need to set again
    } else {
      // No preconditions and no existing session - create default session
      const sessionKey = {
        authProfileId: 'no-auth',
        tenantId: scenario.tenantId || 'default'
      };
      const elseSessionPolicy = scenario.sessionPolicy === 'FRESH' ? 'FRESH' : 'REUSE';
      session = await this.sessionPool.acquireSession(sessionKey, elseSessionPolicy);
      this.testExecutor.setSession(session);
      
      // CRITICAL: Navigate to scenario URL for scenarios without preconditions
      const initialUrl = scenario.url;
      if (initialUrl && initialUrl !== 'about:blank') {
        const host: any = (window as any).host;
        if (host?.navigateSession && session.windowHandle) {
          try {
            await host.navigateSession(session.windowHandle, initialUrl);
            // Wait for page to load
            await new Promise(resolve => setTimeout(resolve, 3000));
          } catch (navError: any) {
            throw new Error(`Navigation failed: ${navError.message}`);
          }
        }
      }
    }

    // Execute scenario steps using the shared execution method
    const scenarioId = scenario.id || scenario.scenarioId;
    const functionalAreaId =
      scenario?.functionalAreaId ||
      scenario?.functionalArea?.id ||
      scenario?.packageId ||
      scenario?.package?.id ||
      undefined;
    const executionRunId = uuidv4();
    const executionContext = { functionalAreaId, scenarioId, executionRunId, tenantId: this.currentRunTenantId || scenario?.tenantId || undefined };
    await this.executeStepsWithMetadata(scenarioId, scenario, apiBase!, session, executionContext);
  }

  /**
   * Shared method to execute scenario steps - used by both single-scenario and job modes
   * @param scenarioId The scenario ID
   * @param scenario The scenario object
   * @param apiBase The API base URL
   * @param sessionState Optional session state (if provided, uses session window; otherwise uses hidden host)
   */
  private async executeStepsWithMetadata(
    scenarioId: string,
    scenario: any,
    apiBase: string,
    sessionState?: any,
    executionContext?: { functionalAreaId?: string; scenarioId?: string; clientPackageMappingId?: string; executionRunId?: string; tenantId?: string }
  ): Promise<void> {
    // ========== FETCH STEPS FROM API ==========
    let steps: any[] = [];
    const base = apiBase.replace(/\/$/, '');
    
    try {
      const stepsResp = await firstValueFrom(this.http.get<any>(`${base}/steps/scenario/${scenarioId}`));
      steps = Array.isArray(stepsResp) ? stepsResp : (stepsResp?.data || []);
    } catch (err: any) {
      throw err;
    }

    // ========== SET EXECUTION CONTEXT ==========
    const functionalAreaId =
      executionContext?.functionalAreaId ||
      scenario?.functionalAreaId ||
      scenario?.functionalArea?.id ||
      scenario?.packageId ||
      scenario?.package?.id ||
      undefined;
    if (!executionContext) {
      const clientPackageMappingId = (scenario as any)?.clientPackageMappingId || (scenario as any)?.clientPackageMapping?.id || (scenario as any)?.functionalArea?.clientPackageMappingId || (scenario as any)?.functionalArea?.clientPackageMapping?.id || undefined;
      this.executionResultService.setExecutionContext(functionalAreaId as any, scenarioId as any, clientPackageMappingId, this.currentRunTenantId || undefined);
    }
    
    // Ensure execution results save to the correct backend
    if (this.executionResultService && (this.executionResultService as any).setApiBase) {
      try {
        (this.executionResultService as any).setApiBase(base);
      } catch {}
    }

    // Ensure report saves go to the correct backend in cloud/runner mode
    if (this.reportApiService && (this.reportApiService as any).setApiBase) {
      try {
        (this.reportApiService as any).setApiBase(base);
      } catch {}
    }

    // ========== BUILD STEP METADATA ==========
    const active = (Array.isArray(steps) ? steps : []).filter((s: any) => s.isActive !== false)
      .sort((a: any, b: any) => (a.stepId || 0) - (b.stepId || 0));
    
    if (!active.length) {
      return; // Nothing to execute
    }

    const inputLines: string[] = active.map((s: any) => s.stepDescription).filter(Boolean);
    const input = inputLines.join('\n');

    const metadata: StepMetadata[] = active.map((s: any, idx: number) => {
      let addInfo: any = undefined;
      if (typeof s.additionalInfo === 'string') {
        try { addInfo = JSON.parse(s.additionalInfo); } catch {}
      } else if (s.additionalInfo && typeof s.additionalInfo === 'object') {
        addInfo = s.additionalInfo;
      }
      if (!addInfo || typeof addInfo !== 'object') addInfo = {};

      const dbIsRecording = !!s.isRecording;
      const selectorHasKeys = !!(addInfo.selector && typeof addInfo.selector === 'object' && Object.keys(addInfo.selector).length > 0);
      const actionDefined = !!addInfo.action;
      const hasRecordedArtifacts = !!(actionDefined || selectorHasKeys);
      const isRecorded = !!(dbIsRecording || addInfo.recordedStep || hasRecordedArtifacts);
      if (isRecorded) addInfo.recordedStep = true;

      return {
        stepId: s.stepId || idx + 1,
        stepUuid: s.id || s.stepUuid || `${s.stepId || idx + 1}`,
        stepDescription: s.stepDescription || s.description || '',
        isRecording: isRecorded,
        additionalInfo: addInfo
      } as StepMetadata;
    });

    // ========== EXECUTE STEPS ==========
    // Set session on singleton only if no explicit context (legacy/single-scenario mode)
    if (sessionState && !executionContext) {
      this.testExecutor.setSession(sessionState);
    }
    let execReport: any;
    let execError: any = null;
    try {
      execReport = await this.testExecutor.executeScenario(input, {
        stepMetadata: metadata,
        delayBetweenSteps: 500,
        navigationDelay: 1000,
        stopOnFailure: true,
        onProgress: () => {},
        session: executionContext ? sessionState : undefined,
        executionContext: executionContext || undefined
      });
    } catch (e: any) {
      execError = e;
      console.error('[RunnerBridge] executeScenario threw:', e?.message || e);
      // Synthesize a minimal report so the failure is still recorded
      execReport = {
        totalSteps: metadata?.length || 0,
        passed: 0,
        failed: metadata?.length || 0,
        stopped: 0
      };
    }

    // Save report record
    if (execReport) {
      try {
        await this.loadReportStatusIds();
        let clientId = scenario?.functionalArea?.clientPackageMapping?.clientId || null;
        let packageId = scenario?.functionalArea?.clientPackageMapping?.packageId || null;
        const clientPackageMappingId = scenario?.functionalArea?.clientPackageMapping?.id || null;

        if (!packageId && scenario?.functionalArea?.package?.id) {
          packageId = scenario.functionalArea.package.id;
        }

        const statusId = (execReport.failed || 0) > 0 ? this.reportStatusFailedId : this.reportStatusPassedId;
        const testTypeId = scenario?.testTypeId || scenario?.functionalArea?.testType?.id || null;

        const tenantId = this.currentRunTenantId || scenario?.tenantId || scenario?.clientTenant?.tenantId || undefined;
        const reportPayload: ReportPayload = {
          clientId,
          packageId,
          clientPackageMappingId,
          functionalAreaId: functionalAreaId || undefined,
          testTypeId: testTypeId || undefined,
          tenantId,
          scenarioId,
          executionId: uuidv4(),
          numberOfSteps: execReport.totalSteps || 0,
          passed: execReport.passed || 0,
          failed: execReport.failed || 0,
          status: statusId || undefined,
          executionStatus: statusId || undefined,
        };
        await firstValueFrom(this.reportApiService.bulkSaveReports([reportPayload]));
      } catch (error: any) {
        console.error('[RunnerBridge] Report save error:', error?.message || error,
          'status:', error?.status,
          'body:', JSON.stringify(error?.error || {}));
      }
    }

    try {
      await this.executionResultService.waitForPendingSaves();
    } catch (saveError: any) {
      // Don't throw - saves failing shouldn't mark execution as failed
    }

    // Re-throw scenario execution error AFTER report save so chain knows it failed
    if (execError) {
      throw execError;
    }
  }

  private async handleRunnerExecute(payload: RunnerPayload) {
    const { runId } = payload;
    this.currentRunTenantId = payload.tenantId || null;
    try {
      // Handle auth token: use provided token or keep existing UI token
      if ('authToken' in payload) {
        if (payload.authToken) {
          localStorage.setItem('authToken', payload.authToken);
          sessionStorage.setItem('authToken', payload.authToken);
        }
      }

      // Fetch scenario and steps; if apiBase provided, fetch directly via HttpClient
      let scenario: any;
      let steps: any[] = [];
      if (payload.apiBase) {
        const base = payload.apiBase.replace(/\/$/, '');
        try {
          scenario = await firstValueFrom(this.http.get(`${base}/scenarios/${runId}`));
        } catch (err: any) {
          throw err;
        }
        try {
          const stepsResp = await firstValueFrom(this.http.get<any>(`${base}/steps/scenario/${runId}`));
          steps = Array.isArray(stepsResp) ? stepsResp : (stepsResp?.data || []);
        } catch (err: any) {
          throw err;
        }
      } else {
        scenario = await firstValueFrom(this.scenarioService.getScenarioById(runId));
        const stepsResp = await firstValueFrom(this.stepsService.getStepsByScenario(runId));
        steps = Array.isArray(stepsResp) ? stepsResp : (stepsResp as any)?.data || [];
      }

      // Set execution context for result persistence (robust mapping)
      // IMPORTANT: Prefer functionalAreaId over packageId to match backend expectations
      const functionalAreaId = 
        (scenario as any)?.functionalAreaId || 
        (scenario as any)?.functionalArea?.id || 
        (scenario as any)?.packageId || 
        (scenario as any)?.package?.id || 
        undefined;
      const scenarioId = (scenario as any)?.id || (scenario as any)?.scenarioId || runId;
      const clientPackageMappingId = (scenario as any)?.clientPackageMappingId || (scenario as any)?.clientPackageMapping?.id || undefined;
      this.executionResultService.setExecutionContext(functionalAreaId as any, scenarioId as any, clientPackageMappingId, this.currentRunTenantId || undefined);

      // Build multi-line input and step metadata for executor
      const active = (Array.isArray(steps) ? steps : []).filter((s: any) => s.isActive !== false)
        .sort((a: any, b: any) => (a.stepId || 0) - (b.stepId || 0));
      const inputLines: string[] = active.map((s: any) => s.stepDescription).filter(Boolean);
      const input = inputLines.join('\n');

      const metadata: StepMetadata[] = active.map((s: any, idx: number) => {
        let addInfo: any = undefined;
        // Normalize additionalInfo to object
        if (typeof s.additionalInfo === 'string') {
          try { addInfo = JSON.parse(s.additionalInfo); } catch {}
        } else if (s.additionalInfo && typeof s.additionalInfo === 'object') {
          addInfo = s.additionalInfo;
        }
        if (!addInfo || typeof addInfo !== 'object') addInfo = {};

        // Robust recorded detection:
        // IMPORTANT: Trust DB flag first to stay consistent with normal execution
        const dbIsRecording = !!s.isRecording;

        // Keep artifact detection as fallback only
        const selectorHasKeys = !!(addInfo.selector && typeof addInfo.selector === 'object' && Object.keys(addInfo.selector).length > 0);
        const actionDefined = !!addInfo.action;
        const hasRecordedArtifacts = !!(actionDefined || selectorHasKeys);

        // IMPORTANT: Trust DB flag first, artifacts second
        const isRecorded = !!(dbIsRecording || addInfo.recordedStep || hasRecordedArtifacts);
        if (isRecorded) addInfo.recordedStep = true;

        return {
          stepId: s.stepId || idx + 1,
          stepUuid: s.id || s.stepUuid || `${s.stepId || idx + 1}`,
          stepDescription: s.stepDescription || s.description || '',
          isRecording: isRecorded,
          additionalInfo: addInfo
        } as StepMetadata;
      });

      // Create hidden host, clear session, navigate, then execute via TestExecutorService
      const host: any = (window as any).host;
      const windowHost: any = (window as any).windowHost;

      const initialUrl = (scenario as any)?.url || 'about:blank';
      // Ensure execution results save to the correct backend when running in cloud mode
      if (payload.apiBase && this.executionResultService && (this.executionResultService as any).setApiBase) {
        try {
          const base = payload.apiBase.replace(/\/$/, '');
          (this.executionResultService as any).setApiBase(base);
        } catch {}
      }

      // Set runtime API base URL for session pool in single-runner mode
      if (payload.apiBase) {
        this.sessionPool.setApiBaseUrl(payload.apiBase.replace(/\/$/, ''));
      }

      // Ensure report saves go to the correct backend in cloud/runner mode
      if (payload.apiBase && this.reportApiService && (this.reportApiService as any).setApiBase) {
        try {
          const base = payload.apiBase.replace(/\/$/, '');
          (this.reportApiService as any).setApiBase(base);
        } catch {}
      }

      // NEW: Check if preconditions are required
      const hasProfileGroup = scenario?.authProfileInfo && (() => {
        try {
          const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
          return !!(info?.profileGroup && info?.authProfileIds?.length > 0);
        } catch { return false; }
      })();
      const hasPreconditions = scenario?.authProfileId || hasProfileGroup || scenario?.startScreenId;
      const useSessionWindow = hasPreconditions;

      let sessionHandle: number | null = null;
      let sessionState: any = null;

      if (useSessionWindow) {
        // Use session window for precondition-based execution
        if (!host?.createSessionWindow) {
          throw new Error('Session window API not available');
        }

        const sessionResult = await host.createSessionWindow({
          partition: `runner-${runId}-${Date.now()}`
        });

        if (!sessionResult.success || !sessionResult.handle) {
          throw new Error(sessionResult.error || 'Failed to create session window');
        }

        sessionHandle = sessionResult.handle;
        // Resolve authProfileId from authProfileInfo when using profile groups
        let singleRunnerAuthProfileId = scenario.authProfileId;
        if (!singleRunnerAuthProfileId && scenario.authProfileInfo) {
          try {
            const info = typeof scenario.authProfileInfo === 'string' ? JSON.parse(scenario.authProfileInfo) : scenario.authProfileInfo;
            if (info?.authProfileIds?.length > 0) {
              singleRunnerAuthProfileId = info.authProfileIds[0];
            }
          } catch { /* ignore parse errors */ }
        }

        sessionState = {
          partition: `runner-${runId}-${Date.now()}`,
          windowHandle: sessionHandle,
          lastUsed: new Date(),
          isLoggedIn: false,
          authProfileId: singleRunnerAuthProfileId,
          tenantId: scenario.tenantId
        };

        // Execute preconditions (login + screen navigation)
        try {
          const preconditionResult = await this.sessionPool.executePreconditions(
            scenario,
            sessionState,
            [] // Empty modules array - will use generated modules from auth profile
          );

          if (!preconditionResult.success) {
            throw new Error(`Precondition failed: ${preconditionResult.error}`);
          }
          
          // Set current session for test executor
          this.testExecutor.setSession(sessionState);

        } catch (preconditionError: any) {
          if (sessionHandle) {
            await host.destroySessionWindow(sessionHandle);
          }
          throw preconditionError;
        }
      } else {
        // Use legacy hidden host for scenarios without preconditions
        if (windowHost?.createHiddenHost) {
          await windowHost.createHiddenHost({ initialUrl });
        }
        if (host?.clearSession) {
          await host.clearSession();
        }
        if (initialUrl && initialUrl !== 'about:blank' && host?.navigate) {
          await host.navigate(initialUrl);
          await this.delay(3000); // Increased from 1s to 3s to match UI mode timing
        }
      }

      let execReport: any;
      let execError: any = null;
      try {
        execReport = await this.testExecutor.executeScenario(input, {
          stepMetadata: metadata,
          delayBetweenSteps: 500,
          navigationDelay: 1000,
          stopOnFailure: true,
          onProgress: () => {}
        });
      } catch (e: any) {
        execError = e;
        console.error('[RunnerBridge] Cloud executeScenario threw:', e?.message || e);
        execReport = {
          totalSteps: metadata?.length || 0,
          passed: 0,
          failed: metadata?.length || 0,
          stopped: 0
        };
      }

      // Save report record (cloud/runner mode)
      if (execReport) {
        try {
          await this.loadReportStatusIds();
          let clientId = (scenario as any)?.functionalArea?.clientPackageMapping?.clientId || null;
          let packageId = (scenario as any)?.functionalArea?.clientPackageMapping?.packageId || null;
          const cpmId = (scenario as any)?.functionalArea?.clientPackageMapping?.id || null;
          const functionalAreaId = (scenario as any)?.functionalAreaId || (scenario as any)?.functionalArea?.id || null;
          if (!packageId && (scenario as any)?.functionalArea?.package?.id) {
            packageId = (scenario as any).functionalArea.package.id;
          }
          const statusId = (execReport.failed || 0) > 0 ? this.reportStatusFailedId : this.reportStatusPassedId;
          const testTypeId = (scenario as any)?.testTypeId || (scenario as any)?.functionalArea?.testType?.id || null;

          const tenantId = this.currentRunTenantId || (scenario as any)?.tenantId || (scenario as any)?.clientTenant?.tenantId || undefined;
          const reportPayload: ReportPayload = {
            clientId,
            packageId,
            clientPackageMappingId: cpmId,
            functionalAreaId: functionalAreaId || undefined,
            testTypeId: testTypeId || undefined,
            tenantId,
            scenarioId,
            executionId: uuidv4(),
            numberOfSteps: execReport.totalSteps || 0,
            passed: execReport.passed || 0,
            failed: execReport.failed || 0,
            status: statusId || undefined,
            executionStatus: statusId || undefined,
          };
          await firstValueFrom(this.reportApiService.bulkSaveReports([reportPayload]));
        } catch (error: any) {
          console.error('[RunnerBridge] Report save error:', error?.message || error);
        }
      }

      // Ensure all execution results are saved before teardown
      await this.executionResultService.waitForPendingSaves();

      // Destroy session window or hidden host
      if (useSessionWindow && sessionHandle) {
        if (host?.destroySessionWindow) {
          await host.destroySessionWindow(sessionHandle);
        }
        // Clear current session from test executor
        this.testExecutor.setSession(null);
      } else if (windowHost?.destroyHiddenHost) {
        await windowHost.destroyHiddenHost();
      }

      if (host?.notifyRunnerDone) {
        host.notifyRunnerDone({ runId });
      }
    } catch (e: any) {
      try {
        const host: any = (window as any).host;
        if (host?.notifyRunnerDone) host.notifyRunnerDone({ runId: payload.runId, error: e?.message || String(e) });
      } catch {}
    }
  }

  private delay(ms: number) { return new Promise(res => setTimeout(res, ms)); }
}